import { useEffect, useMemo, useRef, useState } from 'react'
import Fuse from 'fuse.js'
import {
  Search, Trash2, ShoppingCart, CheckCircle, Loader2, X, Share2,
  AlertTriangle, Receipt, ChevronRight, Package, Tag, Wallet,
  LayoutGrid, Star, Plus, PauseCircle, PlayCircle, CornerDownLeft,
} from 'lucide-react'
import toast from 'react-hot-toast'
import SyncStatusBar from '../components/SyncStatusBar'
import QtyStepper from '../components/QtyStepper'
import Sheet from '../components/Sheet'
import NumPad from '../components/NumPad'
import HelpSheet from '../components/HelpSheet'
import { getAllDrugs, getFavouriteIds, toggleFavourite, listHeld, putHeld, deleteHeld } from '../db'
import { enqueueSale } from '../outbox'
import { generateReceipt, shareReceipt } from '../pdf'
import { getMultiplier, unitOptions, stockInUnit, priceForUnit, defaultUnit, lineTotal as lineTotalOf } from '../units'

const cedi = (n) => '₵' + (Number(n) || 0).toFixed(2)
const QUICK_CASH = [5, 10, 20, 50, 100, 200]

/**
 * Mobile POS — light "premium" register.
 *
 *   ┌────────────────────────┐
 *   │ SyncStatusBar          │ sticky
 *   │ Search ▸ results  [▦]  │  ▦ = Browse catalogue
 *   │ Cart toolbar (count)   │
 *   │ Cart lines  ← scroll   │
 *   │ ───────────────────    │
 *   │ Subtotal / Discount    │ sticky till
 *   │ TOTAL                  │
 *   │ Pay ▸ Tender ▸ Change  │
 *   │ COMPLETE SALE          │
 *   └────────────────────────┘
 *
 * Cart is a Map keyed by drug_id. Each line carries `input_qty` in its
 * `selected_unit`; the BASE-unit quantity the server/desktop ledger needs is
 * always input_qty × getMultiplier(unit) — see units.js for the contract.
 * Get it wrong and offline stock silently corrupts.
 */
export default function PosScreen({ user, pharmacyName, snapshotAt, onLoggedOut, onOpenReceipts }) {
  const [drugs, setDrugs] = useState([])
  const [favIds, setFavIds] = useState(new Set())
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState(new Map())
  const [paymentMode, setPaymentMode] = useState('CASH')
  const [amountPaid, setAmountPaid] = useState('')
  const [discountType, setDiscountType] = useState('AMT') // 'AMT' | 'PCT'
  const [discountValue, setDiscountValue] = useState('')
  const [showDiscount, setShowDiscount] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [lastReceipt, setLastReceipt] = useState(null)
  const [showBrowse, setShowBrowse] = useState(false)
  const [showTender, setShowTender] = useState(false)
  const [held, setHeld] = useState([])
  const [showHeld, setShowHeld] = useState(false)
  const [qtyEdit, setQtyEdit] = useState(null)   // { drugId } being typed
  const [qtyDraft, setQtyDraft] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const searchRef = useRef(null)

  // "/" jumps to search from anywhere on a keyboard, like most POS and
  // dashboard tools — unless the cashier is already typing in a field.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.querySelector('[role="dialog"]')) return
      event.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => { getAllDrugs().then(setDrugs) }, [snapshotAt])
  useEffect(() => { getFavouriteIds().then(setFavIds) }, [])
  useEffect(() => { refreshHeld() }, [])
  const refreshHeld = () => listHeld().then(setHeld)

  const fuse = useMemo(() => new Fuse(drugs, {
    keys: ['brand_name', 'generic_name'],
    threshold: 0.35,
    minMatchCharLength: 2,
  }), [drugs])

  // Full ranked results feed the desktop catalogue grid; the phone dropdown
  // shows the top 20.
  const results = useMemo(() => {
    if (!search.trim()) return []
    return fuse.search(search.trim()).map(r => r.item)
  }, [search, fuse])
  const matches = useMemo(() => results.slice(0, 20), [results])

  // Enter adds the best in-stock match, so a cashier who knows the name can
  // ring up a sale without leaving the keyboard. Escape clears the search.
  const onSearchKeyDown = (event) => {
    if (event.key === 'Escape') { setSearch(''); return }
    if (event.key !== 'Enter') return
    event.preventDefault()
    const top = results.find(d => (Number(d.total_stock) || 0) > 0)
    if (!top) { if (results.length) toast.error('Out of stock'); return }
    addToCart(top)
    setSearch('')
    toast.success(`Added ${top.brand_name}`, { duration: 1200 })
  }

  const favDrugs = useMemo(
    () => drugs.filter(d => favIds.has(d.drug_id)),
    [drugs, favIds]
  )

  // ── Cart operations ────────────────────────────────────────────────────────

  const addToCart = (drug) => {
    const stock = Number(drug.total_stock) || 0
    if (stock <= 0) { toast.error('Out of stock'); return }
    setCart(prev => {
      const next = new Map(prev)
      const existing = next.get(drug.drug_id)
      if (existing) {
        const mult = getMultiplier(existing.selected_unit, existing)
        const nextInput = existing.input_qty + 1
        if (nextInput * mult > existing.total_stock) {
          toast.error(`Only ${stockInUnit(existing, existing.selected_unit)} ${existing.selected_unit} left`)
          return prev
        }
        next.set(drug.drug_id, { ...existing, input_qty: nextInput })
      } else {
        // Start in the configured default unit, but fall back to base if it's
        // not a sellable unit for this drug, or a single pack already exceeds
        // stock (mirrors desktop handleAddDrug).
        const opts = unitOptions(drug)
        let unit = defaultUnit(drug)
        if (!opts.some(o => o.value === unit) || getMultiplier(unit, drug) > stock) {
          unit = opts[0]?.value || drug.base_unit || 'Tab'
        }
        next.set(drug.drug_id, {
          drug_id: drug.drug_id,
          brand_name: drug.brand_name,
          generic_name: drug.generic_name,
          dosage_form: drug.dosage_form,
          strength: drug.strength,
          base_unit: drug.base_unit || 'Tab',
          pack_unit: drug.pack_unit,
          pack_size: drug.pack_size,
          selling_price: Number(drug.selling_price) || 0,
          units: drug.units || null,
          total_stock: stock,
          input_qty: 1,
          selected_unit: unit,
        })
      }
      return next
    })
  }

  const addFromSearch = (drug) => {
    addToCart(drug)
    setSearch('')
    searchRef.current?.blur()
  }

  const setQty = (drugId, nextInput) => {
    setCart(prev => {
      const next = new Map(prev)
      const item = next.get(drugId)
      if (!item) return prev
      const maxU = stockInUnit(item, item.selected_unit)
      let q = Math.floor(Number(nextInput) || 0)
      if (q > maxU) { toast.error(`Only ${maxU} ${item.selected_unit} left`); q = maxU }
      if (q < 1) q = 1
      next.set(drugId, { ...item, input_qty: q })
      return next
    })
  }

  const setUnit = (drugId, unit) => {
    setCart(prev => {
      const next = new Map(prev)
      const item = next.get(drugId)
      if (!item) return prev
      const maxU = stockInUnit(item, unit)
      if (maxU < 1) { toast.error(`Not enough stock for 1 ${unit}`); return prev }
      next.set(drugId, { ...item, selected_unit: unit, input_qty: Math.min(item.input_qty, maxU) })
      return next
    })
  }

  const removeItem = (drugId) =>
    setCart(prev => { const n = new Map(prev); n.delete(drugId); return n })

  // Clearing a cart by accident mid-queue is costly, so Clear offers an Undo
  // instead of a confirm dialog (which would slow every intentional clear).
  const clearSale = () => {
    const snapshot = { cart, discountType, discountValue, showDiscount }
    resetSale()
    toast((t) => (
      <span className="flex items-center gap-3">
        Sale cleared
        <button
          type="button"
          onClick={() => {
            setCart(snapshot.cart)
            setDiscountType(snapshot.discountType)
            setDiscountValue(snapshot.discountValue)
            setShowDiscount(snapshot.showDiscount)
            toast.dismiss(t.id)
          }}
          className="px-3 py-1.5 rounded-lg bg-lime text-brand-800 text-sm font-semibold"
        >
          Undo
        </button>
      </span>
    ), { duration: 5000 })
  }

  const resetSale = () => {
    setCart(new Map())
    setAmountPaid('')
    setPaymentMode('CASH')
    setDiscountType('AMT')
    setDiscountValue('')
    setShowDiscount(false)
  }

  // ── Hold / park ──────────────────────────────────────────────────────────────

  const holdSale = async () => {
    if (cart.size === 0) return
    const lines = Array.from(cart.values())
    const label = lines[0]?.brand_name
      ? `${lines[0].brand_name}${lines.length > 1 ? ` +${lines.length - 1}` : ''}`
      : 'Sale'
    await putHeld({
      id: crypto.randomUUID(),
      label,
      created_at: Date.now(),
      lines,
      discount_type: discountType,
      discount_value: discountValue,
    })
    resetSale()
    refreshHeld()
    toast.success('Sale held')
  }

  const resumeHeld = (rec) => {
    if (cart.size > 0) { toast.error('Finish or hold the current sale first'); return }
    // Re-hydrate prices/stock from the latest catalogue so a parked cart never
    // resumes with a stale price or oversells against fresh stock.
    const byId = new Map(drugs.map(d => [d.drug_id, d]))
    const next = new Map()
    let dropped = 0
    for (const line of rec.lines) {
      const fresh = byId.get(line.drug_id)
      if (!fresh || (Number(fresh.total_stock) || 0) <= 0) { dropped++; continue }
      const merged = {
        ...line,
        selling_price: Number(fresh.selling_price) || 0,
        units: fresh.units || null,
        total_stock: Number(fresh.total_stock) || 0,
        pack_unit: fresh.pack_unit,
        pack_size: fresh.pack_size,
      }
      const maxU = stockInUnit(merged, merged.selected_unit)
      merged.input_qty = Math.max(1, Math.min(merged.input_qty, maxU || 1))
      next.set(merged.drug_id, merged)
    }
    setCart(next)
    setDiscountType(rec.discount_type || 'AMT')
    setDiscountValue(rec.discount_value || '')
    deleteHeld(rec.id).then(refreshHeld)
    setShowHeld(false)
    if (dropped) toast(`${dropped} item${dropped === 1 ? '' : 's'} dropped (out of stock)`, { icon: '⚠️' })
  }

  const dropHeld = (id) => deleteHeld(id).then(refreshHeld)

  // ── Favourites ───────────────────────────────────────────────────────────────

  const onToggleFav = async (drugId) => {
    const nowFav = await toggleFavourite(drugId)
    setFavIds(prev => {
      const n = new Set(prev)
      if (nowFav) n.add(drugId); else n.delete(drugId)
      return n
    })
  }

  // ── Quantity keypad ──────────────────────────────────────────────────────────

  const openQtyPad = (item) => { setQtyEdit(item.drug_id); setQtyDraft(String(item.input_qty)) }
  const qtyItem = qtyEdit != null ? cart.get(qtyEdit) : null
  const qtyMaxUnits = qtyItem ? stockInUnit(qtyItem, qtyItem.selected_unit) : 0
  const qtyPreviewN = Math.max(0, Math.floor(Number(qtyDraft) || 0))
  const commitQty = () => {
    if (qtyItem) setQty(qtyItem.drug_id, qtyPreviewN || 1)
    setQtyEdit(null)
  }

  // ── Totals ──────────────────────────────────────────────────────────────────

  const subtotal = useMemo(() => {
    let s = 0
    for (const it of cart.values()) s += lineTotalOf(it)
    return Math.round(s * 100) / 100
  }, [cart])

  const discountAmount = useMemo(() => {
    const v = parseFloat(discountValue) || 0
    const raw = discountType === 'PCT' ? (subtotal * v) / 100 : v
    return Math.min(Math.max(0, raw), subtotal)
  }, [discountValue, discountType, subtotal])

  const total = subtotal - discountAmount
  const paidNum = parseFloat(amountPaid) || 0
  const change = paymentMode === 'CASH' && paidNum >= total ? paidNum - total : 0
  const short = paymentMode === 'CASH' && amountPaid !== '' && paidNum < total

  // ── Checkout ──────────────────────────────────────────────────────────────

  const handleComplete = async () => {
    if (cart.size === 0) return
    if (short) { toast.error('Amount received is less than total'); return }
    setSubmitting(true)
    try {
      const items = Array.from(cart.values()).map(it => {
        const mult = getMultiplier(it.selected_unit, it)
        const baseQty = it.input_qty * mult
        const total = lineTotalOf(it)
        return {
          drug_id: it.drug_id,
          brand_name: it.brand_name,
          generic_name: it.generic_name,
          dosage_form: it.dosage_form,
          strength: it.strength,
          base_unit: it.base_unit,
          selected_unit: it.selected_unit,
          input_qty: it.input_qty,
          quantity: baseQty,                       // BASE units — desktop deducts this
          unit_price: baseQty > 0 ? total / baseQty : 0, // effective price per base unit
          line_total: total,
          sale_unit: it.selected_unit,
          sale_unit_price: priceForUnit(it, it.selected_unit),
        }
      })
      const paid = paymentMode === 'CASH' ? (amountPaid !== '' ? paidNum : total) : total
      const payload = {
        client_op_id: crypto.randomUUID(),
        payment_mode: paymentMode,
        subtotal,
        discount_amount: discountAmount,
        total_amount: total,
        amount_paid: paid,
        change_given: paymentMode === 'CASH' ? Math.max(0, paid - total) : 0,
        items,
        occurred_at: new Date().toISOString(),
        cashier_name: user?.full_name || user?.username,
        device_id: getDeviceId(),
        device_label: navigator.userAgent.slice(0, 80),
      }

      await enqueueSale(payload)
      const blob = await generateReceipt({ payload, pharmacyName })
      setLastReceipt({ blob, payload })

      resetSale()
      toast.success(`Sale recorded · ${cedi(total)}`)
      getAllDrugs().then(setDrugs)

      shareReceipt(blob, payload.client_op_id).catch(err => console.warn('auto-share failed:', err))
    } catch (err) {
      toast.error(err?.message || 'Could not record sale')
    } finally {
      setSubmitting(false)
    }
  }

  const handleShare = async () => {
    if (!lastReceipt) return
    await shareReceipt(lastReceipt.blob, lastReceipt.payload.client_op_id)
  }

  const cartCount = cart.size

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="pc-register-shell h-full flex flex-col lg:flex-row text-sage-900 overflow-hidden">
      {/* Desktop side rail — renders hidden lg:flex inside the component */}
      <SyncStatusBar
        variant="rail"
        snapshotAt={snapshotAt}
        pharmacyName={pharmacyName}
        user={user}
        onLoggedOut={onLoggedOut}
        onOpenReceipts={onOpenReceipts}
        onOpenHelp={() => setShowHelp(true)}
        onFocusSearch={() => searchRef.current?.focus()}
      />

      <main className="flex-1 min-w-0 flex flex-col min-h-0">
        <h1 className="sr-only lg:hidden">Offline register: new sale</h1>
        {/* Phone top bar */}
        <div className="lg:hidden shrink-0">
          <SyncStatusBar
            snapshotAt={snapshotAt}
            pharmacyName={pharmacyName}
            user={user}
            onLoggedOut={onLoggedOut}
            onOpenReceipts={onOpenReceipts}
            onOpenHelp={() => setShowHelp(true)}
          />
        </div>

        {/* On phones this stays one column (the two wrappers below render as
            display:contents, so the DOM flows exactly as before). At lg+ it
            becomes a two-panel register: catalogue left, cart + till right. */}
        <div className="pc-register-grid flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(390px,30rem)] lg:gap-4 lg:p-4 lg:w-full lg:max-w-[1400px] lg:mx-auto">
          <div className="contents lg:flex lg:flex-col lg:min-h-0">

      {/* Search + Browse */}
      <div className="pc-drug-search shrink-0 px-3 pt-3 pb-2 lg:px-0 lg:pt-0">
        <div className="hidden lg:flex items-end justify-between mb-3 px-0.5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600">New sale</p>
            <h1 className="font-display text-[22px] font-semibold text-brand-800 mt-0.5">Find and add medicine</h1>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-sage-600">
            <kbd className="px-1.5 py-0.5 rounded-md bg-white ring-1 ring-sage-200 font-mono text-[11px]">/</kbd> search
            <span aria-hidden="true">·</span>
            <kbd className="px-1.5 py-0.5 rounded-md bg-white ring-1 ring-sage-200 font-mono text-[11px] inline-flex items-center"><CornerDownLeft size={11} /></kbd> adds the top result
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="pc-search-field flex-1 min-h-[52px] flex items-center bg-white ring-1 ring-line-strong rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-brand-600">
            <Search size={18} className="text-sage-400 ml-3.5 shrink-0" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Search brand or generic name…"
              aria-label="Search medicines"
              enterKeyHint="done"
              className="flex-1 min-w-0 px-3 py-3.5 bg-transparent text-base font-semibold text-sage-900 placeholder:text-sage-400 placeholder:font-medium focus:outline-none [&::-webkit-search-cancel-button]:hidden"
              autoCapitalize="none"
              autoCorrect="off"
            />
            {search && (
              <button onClick={() => setSearch('')} aria-label="Clear search" className="h-11 w-11 mr-0.5 grid place-items-center rounded-xl text-sage-500 hover:bg-sage-100 active:text-sage-800">
                <X size={17} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowBrowse(true)}
            aria-label="Browse catalogue"
            className="lg:hidden shrink-0 h-[52px] w-[52px] grid place-items-center rounded-2xl bg-white ring-1 ring-line-strong shadow-sm text-brand-600 active:bg-sage-50"
          >
            <LayoutGrid size={20} />
          </button>
        </div>

        {search.trim().length >= 2 && results.length === 0 && (
          <p className="lg:hidden mt-2 px-1 text-sm text-sage-600" role="status">No medicines match “{search.trim()}”.</p>
        )}
        {matches.length > 0 && (
          <div className="lg:hidden mt-2 max-h-72 overflow-y-auto rounded-2xl ring-1 ring-sage-200 bg-white shadow-lg divide-y divide-sage-100 pc-pop">
            {matches.map(d => {
              const stock = Number(d.total_stock) || 0
              return (
                <button
                  key={d.drug_id}
                  onClick={() => addFromSearch(d)}
                  disabled={stock <= 0}
                  className="w-full text-left px-3.5 py-3 active:bg-brand-50 disabled:opacity-40 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-sage-900 truncate">{d.brand_name}</p>
                    <p className="text-xs text-sage-500 truncate">
                      {[d.generic_name, [d.dosage_form, d.strength].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-black text-accent tabular-nums">{cedi(priceForUnit(d, defaultUnit(d)))}</p>
                    <p className={`text-xs font-bold ${stock < 20 ? 'text-rose-600' : 'text-sage-400'}`}>
                      {stock} {d.base_unit || 'Tab'}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

          {/* Desktop catalogue panel — phones use the Browse sheet instead */}
          <div className="pc-catalogue-panel hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:mt-3 lg:bg-white/70 lg:rounded-2xl lg:ring-1 lg:ring-sage-200 lg:overflow-hidden">
            <CataloguePanel drugs={drugs} query={search.trim()} results={results} favIds={favIds} onAdd={addToCart} onToggleFav={onToggleFav} />
          </div>
          </div>

          {/* Register column: cart + till */}
          <div className="pc-sale-panel contents lg:flex lg:flex-col lg:min-h-0 lg:bg-white lg:rounded-2xl lg:ring-1 lg:ring-sage-200 lg:shadow-[0_18px_50px_-24px_rgba(1,42,28,0.25)] lg:overflow-hidden">

      {/* Cart toolbar */}
      {(cartCount > 0 || held.length > 0) && (
        <div className="pc-cart-toolbar shrink-0 flex items-center justify-between px-4 pb-1.5 gap-2 lg:pt-3.5">
          <span className="text-xs font-bold uppercase tracking-widest text-sage-500">
            {cartCount > 0 ? `Cart · ${cartCount} item${cartCount === 1 ? '' : 's'}` : 'Cart'}
          </span>
          <div className="flex items-center gap-1.5">
            {held.length > 0 && (
              <button
                onClick={() => setShowHeld(true)}
                className="flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2.5 py-1 rounded-full active:bg-amber-100"
              >
                <PauseCircle size={12} /> Held · {held.length}
              </button>
            )}
            {cartCount > 0 && (
              <>
                <button
                  onClick={holdSale}
                  className="flex items-center gap-1 text-xs font-bold text-sage-600 bg-sage-100 ring-1 ring-sage-200 px-2.5 py-1 rounded-full active:bg-sage-200"
                >
                  <PauseCircle size={12} /> Hold
                </button>
                <button
                  onClick={clearSale}
                  className="flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 ring-1 ring-rose-100 px-2.5 py-1 rounded-full active:bg-rose-100"
                >
                  <Trash2 size={12} /> Clear
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cart */}
      <div className="pc-cart-body flex-1 overflow-y-auto px-3 pb-3 min-h-0">
        {cartCount === 0 ? (
          <EmptyCart
            hasReceipt={!!lastReceipt}
            onShare={handleShare}
            receipt={lastReceipt}
            drugCount={drugs.length}
            favDrugs={favDrugs}
            onAddFav={addToCart}
            onBrowse={() => setShowBrowse(true)}
            heldCount={held.length}
            onOpenHeld={() => setShowHeld(true)}
            onOpenReceipts={onOpenReceipts}
            onFocusSearch={() => searchRef.current?.focus()}
          />
        ) : (
          <div className="space-y-2">
            {Array.from(cart.values()).map(item => (
              <CartRow
                key={item.drug_id}
                item={item}
                onQty={(q) => setQty(item.drug_id, q)}
                onUnit={(u) => setUnit(item.drug_id, u)}
                onTapQty={() => openQtyPad(item)}
                onRemove={() => removeItem(item.drug_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky till footer */}
      {cartCount > 0 && (
        <div
          className="pc-till shrink-0 bg-white border-t border-sage-200 shadow-[0_-10px_28px_-14px_rgba(1,42,28,0.22)] px-3 pt-3 space-y-2.5"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
        >
          {/* Subtotal + discount */}
          <div className="space-y-1.5">
            <Row label="Subtotal" value={cedi(subtotal)} muted />
            {showDiscount || discountAmount > 0 ? (
              <DiscountEditor
                type={discountType} setType={setDiscountType}
                value={discountValue} setValue={setDiscountValue}
                amount={discountAmount}
                onClose={() => { setShowDiscount(false); setDiscountValue('') }}
              />
            ) : (
              <button
                onClick={() => setShowDiscount(true)}
                className="flex items-center gap-1.5 text-[12px] font-bold text-brand-600 active:text-brand-700"
              >
                <Tag size={13} /> Add discount
              </button>
            )}
          </div>

          <div className="h-px bg-sage-200" />

          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-sage-500 uppercase tracking-widest">Total</span>
            <span className="text-[2rem] leading-none font-black text-brand-800 tabular-nums tracking-tight">{cedi(total)}</span>
          </div>

          {/* Payment method */}
          <div className="grid grid-cols-3 gap-2">
            {[['CASH', 'Cash'], ['MOMO_MTN', 'MoMo'], ['CARD', 'Card']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setPaymentMode(mode)}
                className={`py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors ${
                  paymentMode === mode
                    ? 'bg-brand-600 text-white shadow-sm shadow-brand-600/25'
                    : 'bg-sage-100 text-sage-500 ring-1 ring-sage-200 active:bg-sage-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Cash tender — compact; the keypad lives in a sheet to keep the till tidy */}
          {paymentMode === 'CASH' && (
            <button
              onClick={() => setShowTender(true)}
              className="w-full flex items-center gap-2.5 px-3.5 py-3 rounded-xl bg-sage-100 ring-1 ring-sage-200 active:bg-sage-200"
            >
              <Wallet size={16} className="text-sage-400 shrink-0" />
              <span className="text-sm font-bold text-sage-500">Cash received</span>
              <span className={`ml-auto text-base font-black tabular-nums ${amountPaid !== '' ? 'text-sage-900' : 'text-sage-400'}`}>
                {amountPaid !== '' ? cedi(paidNum) : 'Tap to enter'}
              </span>
              {amountPaid !== '' && !short && change > 0 && (
                <span className="text-xs font-bold text-brand-700 tabular-nums shrink-0">Chg {cedi(change)}</span>
              )}
              {short && (
                <span className="text-xs font-bold text-rose-600 shrink-0">short</span>
              )}
            </button>
          )}

          <button
            onClick={handleComplete}
            disabled={submitting}
            className="w-full h-14 rounded-2xl bg-brand-600 text-white text-base font-black shadow-lg shadow-brand-600/25 active:bg-brand-700 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting
              ? <><Loader2 size={19} className="animate-spin" /> Recording…</>
              : <><CheckCircle size={19} strokeWidth={2.5} /> Complete · {cedi(total)}</>}
          </button>
        </div>
      )}
          </div>
        </div>
      </main>

      {/* Browse catalogue sheet */}
      <BrowseSheet
        open={showBrowse}
        onClose={() => setShowBrowse(false)}
        drugs={drugs}
        favIds={favIds}
        onAdd={(d) => { addToCart(d); toast.success(`Added ${d.brand_name}`, { duration: 1200 }) }}
        onToggleFav={onToggleFav}
      />

      {/* Quantity keypad sheet */}
      <Sheet
        open={qtyEdit != null}
        onClose={() => setQtyEdit(null)}
        title={qtyItem?.brand_name}
        subtitle={qtyItem ? `${qtyMaxUnits} ${qtyItem.selected_unit} in stock` : ''}
        footer={
          <button
            onClick={commitQty}
            disabled={qtyPreviewN < 1 || qtyPreviewN > qtyMaxUnits}
            className="w-full py-3.5 rounded-2xl bg-brand-600 text-white text-base font-black active:bg-brand-700 disabled:opacity-40"
          >
            Set quantity
          </button>
        }
      >
        {qtyItem && (
          <div className="pb-1">
            <div className="flex items-baseline justify-between mb-3 px-1">
              <div>
                <p className="text-4xl font-black tabular-nums text-sage-900">{qtyDraft || '0'}</p>
                <p className="text-xs font-semibold text-sage-500 mt-0.5">{qtyItem.selected_unit}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black tabular-nums text-accent">
                  {cedi(qtyPreviewN * priceForUnit(qtyItem, qtyItem.selected_unit))}
                </p>
                <p className="text-xs font-semibold text-sage-400 mt-0.5">
                  = {qtyPreviewN * getMultiplier(qtyItem.selected_unit, qtyItem)} {qtyItem.base_unit}
                </p>
              </div>
            </div>
            {qtyPreviewN > qtyMaxUnits && (
              <p className="flex items-center gap-1 text-[12px] font-bold text-rose-600 mb-2 px-1">
                <AlertTriangle size={12} /> Only {qtyMaxUnits} {qtyItem.selected_unit} in stock
              </p>
            )}
            <NumPad value={qtyDraft} onChange={setQtyDraft} />
          </div>
        )}
      </Sheet>

      {/* Cash tender keypad sheet */}
      <Sheet
        open={showTender}
        onClose={() => setShowTender(false)}
        title="Cash received"
        subtitle={`Total ${cedi(total)}`}
        footer={
          <button
            onClick={() => setShowTender(false)}
            className="w-full py-3.5 rounded-2xl bg-brand-600 text-white text-base font-black active:bg-brand-700"
          >
            Done
          </button>
        }
      >
        <div className="pb-1">
          <div className="flex items-baseline justify-between mb-3 px-1">
            <div>
              <p className="text-4xl font-black tabular-nums text-sage-900">{amountPaid !== '' ? cedi(paidNum) : '₵0.00'}</p>
              <p className="text-xs font-semibold text-sage-500 mt-0.5">Cash received</p>
            </div>
            <div className="text-right">
              {short ? (
                <>
                  <p className="text-2xl font-black tabular-nums text-rose-600">{cedi(total - paidNum)}</p>
                  <p className="text-xs font-semibold text-rose-600 mt-0.5">short</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-black tabular-nums text-accent">{cedi(change)}</p>
                  <p className="text-xs font-semibold text-sage-400 mt-0.5">change</p>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-2">
            <button
              onClick={() => setAmountPaid(total.toFixed(2))}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-200 text-xs font-black active:bg-brand-100"
            >
              Exact
            </button>
            {QUICK_CASH.map(n => (
              <button
                key={n}
                onClick={() => setAmountPaid(((parseFloat(amountPaid) || 0) + n).toString())}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-sage-100 text-sage-600 ring-1 ring-sage-200 text-xs font-bold tabular-nums active:bg-sage-200"
              >
                +{n}
              </button>
            ))}
            {amountPaid !== '' && (
              <button
                onClick={() => setAmountPaid('')}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-sage-100 text-sage-500 ring-1 ring-sage-200 text-xs font-bold active:bg-sage-200"
              >
                Clear
              </button>
            )}
          </div>
          <NumPad value={amountPaid} onChange={setAmountPaid} decimal />
        </div>
      </Sheet>

      <HelpSheet open={showHelp} onClose={() => setShowHelp(false)} />

      {/* Held / parked sales sheet */}
      <Sheet
        open={showHeld}
        onClose={() => setShowHeld(false)}
        title={`Held sales · ${held.length}`}
        subtitle="Tap to resume, or delete"
      >
        <div className="space-y-2 pb-2">
          {held.length === 0 ? (
            <p className="text-center text-sm text-sage-400 py-10">No held sales.</p>
          ) : (
            held.map(rec => {
              const count = rec.lines?.length || 0
              const sum = (rec.lines || []).reduce(
                (s, l) => s + lineTotalOf(l), 0)
              return (
                <div key={rec.id} className="flex items-center gap-2 bg-white ring-1 ring-sage-200 rounded-2xl p-3 shadow-sm">
                  <button onClick={() => resumeHeld(rec)} className="flex-1 min-w-0 flex items-center gap-3 text-left">
                    <PlayCircle size={24} className="text-brand-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-sage-900 truncate">{rec.label}</p>
                      <p className="text-xs text-sage-500">
                        {count} item{count === 1 ? '' : 's'} · {cedi(sum)} · {timeAgo(rec.created_at)}
                      </p>
                    </div>
                  </button>
                  <button onClick={() => dropHeld(rec.id)} aria-label="Delete held sale" className="p-2 text-sage-400 active:text-rose-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              )
            })
          )}
        </div>
      </Sheet>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-[13px] font-semibold ${muted ? 'text-sage-500' : 'text-sage-700'}`}>{label}</span>
      <span className="text-[15px] font-bold text-sage-700 tabular-nums">{value}</span>
    </div>
  )
}

function CartRow({ item, onQty, onUnit, onTapQty, onRemove }) {
  const opts = unitOptions(item)
  const lineTotal = lineTotalOf(item)
  const maxUnits = stockInUnit(item, item.selected_unit)
  const sub = [item.dosage_form, item.strength].filter(Boolean).join(' ')

  return (
    <article className="pc-cart-line bg-white ring-1 ring-sage-200 rounded-2xl p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-sage-900 truncate">{item.brand_name}</p>
          <p className="text-xs text-sage-500 truncate">
            {[item.generic_name, sub].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button onClick={onRemove} aria-label="Remove" className="p-1.5 -mt-0.5 -mr-1 text-sage-400 active:text-rose-600 rounded-lg">
          <Trash2 size={16} />
        </button>
      </div>

      {/* Unit chips (only when this drug sells in more than one unit) */}
      {opts.length > 1 && (
        <div className="mt-2.5 flex items-center gap-1.5 overflow-x-auto -mx-0.5 px-0.5 pb-0.5">
          {opts.map(o => (
            <button
              key={o.value}
              onClick={() => onUnit(o.value)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                item.selected_unit === o.value
                  ? 'bg-brand-600 text-white'
                  : 'bg-sage-100 text-sage-600 ring-1 ring-sage-200 active:bg-sage-200'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        {opts.length <= 1 && (
          <span className="shrink-0 h-11 grid place-items-center px-3 rounded-xl bg-sage-100 text-sage-500 text-xs font-bold ring-1 ring-sage-200">
            {item.base_unit || 'Tab'}
          </span>
        )}
        <QtyStepper value={item.input_qty} max={maxUnits} onChange={onQty} onTap={onTapQty} />
        <div className="ml-auto text-right">
          <p className="text-lg font-black text-accent tabular-nums leading-none">{cedi(lineTotal)}</p>
          <p className="text-xs text-sage-400 mt-1 tabular-nums">
            {cedi(priceForUnit(item, item.selected_unit))} × {item.input_qty}
          </p>
        </div>
      </div>
    </article>
  )
}

function DiscountEditor({ type, setType, value, setValue, amount, onClose }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex rounded-lg overflow-hidden ring-1 ring-sage-200">
        {[['AMT', '₵'], ['PCT', '%']].map(([t, sym]) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`w-8 h-9 text-sm font-black ${type === t ? 'bg-brand-600 text-white' : 'bg-sage-100 text-sage-500'}`}
          >
            {sym}
          </button>
        ))}
      </div>
      <input
        inputMode="decimal"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={type === 'PCT' ? '0%' : '0.00'}
        autoFocus
        className="w-20 h-9 px-2.5 rounded-lg bg-sage-100 ring-1 ring-sage-200 text-sm font-bold text-sage-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500/40"
      />
      <span className="text-[13px] font-bold text-rose-600 tabular-nums">− {cedi(amount)}</span>
      <button onClick={onClose} className="ml-auto p-1.5 text-sage-400 active:text-sage-700">
        <X size={15} />
      </button>
    </div>
  )
}

/**
 * BrowseSheet — tap-to-add catalogue, so staff can build a cart without typing.
 * Favourites pin to the top; everything else is alphabetical and capped to keep
 * a big catalogue smooth (a search box narrows it past the cap).
 */
function BrowseSheet({ open, onClose, drugs, favIds, onAdd, onToggleFav }) {
  const [q, setQ] = useState('')
  const CAP = 100

  const favList = useMemo(
    () => drugs.filter(d => favIds.has(d.drug_id)).sort(byName),
    [drugs, favIds]
  )

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    let list = drugs
    if (term) {
      list = drugs.filter(d =>
        (d.brand_name || '').toLowerCase().includes(term) ||
        (d.generic_name || '').toLowerCase().includes(term)
      )
    }
    return [...list].sort(byName)
  }, [drugs, q])

  const shown = filtered.slice(0, CAP)
  const overflow = filtered.length - shown.length

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Browse drugs"
      subtitle={`${drugs.length.toLocaleString()} in catalogue`}
      maxHeight="92%"
    >
      <div className="sticky top-0 bg-white pb-2 z-10">
        <div className="flex items-center bg-sage-100 ring-1 ring-sage-200 rounded-xl focus-within:ring-2 focus-within:ring-brand-500/40">
          <Search size={16} className="text-sage-400 ml-3" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Filter by name…"
            autoCapitalize="none"
            autoCorrect="off"
            className="flex-1 px-2.5 py-2.5 bg-transparent text-sm font-semibold text-sage-900 placeholder:text-sage-400 focus:outline-none"
          />
          {q && (
            <button onClick={() => setQ('')} className="px-3 text-sage-400 active:text-sage-700"><X size={15} /></button>
          )}
        </div>
      </div>

      {!q && favList.length > 0 && (
        <div className="mb-2">
          <p className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-amber-700 mb-1.5 px-0.5">
            <Star size={11} className="fill-amber-400 text-amber-400" /> Favourites
          </p>
          <div className="space-y-1.5">
            {favList.map(d => (
              <BrowseRow key={d.drug_id} d={d} isFav onAdd={onAdd} onToggleFav={onToggleFav} />
            ))}
          </div>
          <div className="h-px bg-sage-100 my-3" />
        </div>
      )}

      <div className="space-y-1.5 pb-2">
        {shown.length === 0 ? (
          <p className="text-center text-sm text-sage-400 py-10">No drugs match “{q}”.</p>
        ) : (
          shown.map(d => (
            <BrowseRow key={d.drug_id} d={d} isFav={favIds.has(d.drug_id)} onAdd={onAdd} onToggleFav={onToggleFav} />
          ))
        )}
        {overflow > 0 && (
          <p className="text-center text-xs font-semibold text-sage-400 pt-2">
            +{overflow.toLocaleString()} more. Type to filter.
          </p>
        )}
      </div>
    </Sheet>
  )
}

function BrowseRow({ d, isFav, onAdd, onToggleFav }) {
  const stock = Number(d.total_stock) || 0
  const oos = stock <= 0
  return (
    <div className={`flex items-center gap-2 rounded-xl ring-1 ring-sage-200 p-2.5 ${oos ? 'bg-sage-50 opacity-60' : 'bg-white'}`}>
      <button
        onClick={() => onToggleFav(d.drug_id)}
        aria-label={isFav ? 'Unpin' : 'Pin to favourites'}
        className="p-1.5 shrink-0"
      >
        <Star size={17} className={isFav ? 'fill-amber-400 text-amber-400' : 'text-sage-300'} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-sage-900 truncate">{d.brand_name}</p>
        <p className="text-xs text-sage-500 truncate">
          {[d.generic_name, [d.dosage_form, d.strength].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-black text-accent tabular-nums">{cedi(priceForUnit(d, defaultUnit(d)))}</p>
        <p className={`text-xs font-bold ${oos ? 'text-rose-600' : stock < 20 ? 'text-amber-700' : 'text-sage-400'}`}>
          {oos ? 'Out' : `${stock} ${d.base_unit || 'Tab'}`}
        </p>
      </div>
      <button
        onClick={() => onAdd(d)}
        disabled={oos}
        aria-label="Add"
        className="shrink-0 h-9 w-9 grid place-items-center rounded-xl bg-brand-600 text-white active:bg-brand-700 disabled:opacity-30"
      >
        <Plus size={18} strokeWidth={2.75} />
      </button>
    </div>
  )
}

function EmptyCart({ hasReceipt, receipt, onShare, drugCount, favDrugs, onAddFav, onBrowse, heldCount, onOpenHeld, onOpenReceipts, onFocusSearch }) {
  return (
    <div className="pc-empty-sale h-full flex flex-col items-center justify-center text-center p-6 gap-3">
      <div className="pc-empty-sale-icon w-16 h-16 rounded-2xl bg-brand-50 ring-1 ring-brand-100 shadow-sm grid place-items-center">
        <ShoppingCart size={28} className="text-brand-600" />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.17em] text-brand-600">Current sale</p>
        <p className="font-display text-[22px] font-semibold text-brand-800 mt-1">Ready for the next customer</p>
        <p className="text-xs text-sage-500 mt-1.5 max-w-[18rem]">
          Search or browse the saved catalogue, select the unit and quantity, then take payment.
        </p>
      </div>

      {heldCount > 0 && (
        <button
          onClick={onOpenHeld}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-sm font-bold text-amber-700 active:bg-amber-100"
        >
          <PauseCircle size={15} /> Resume held sale · {heldCount}
        </button>
      )}

      {/* Favourites quick-add */}
      {favDrugs.length > 0 && (
        <div className="w-full max-w-sm">
          <p className="flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest text-amber-700 mb-1.5">
            <Star size={11} className="fill-amber-400 text-amber-400" /> Favourites
          </p>
          <div className="flex flex-wrap justify-center gap-1.5">
            {favDrugs.slice(0, 8).map(d => {
              const oos = (Number(d.total_stock) || 0) <= 0
              return (
                <button
                  key={d.drug_id}
                  onClick={() => onAddFav(d)}
                  disabled={oos}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white ring-1 ring-sage-200 shadow-sm text-xs font-bold text-sage-700 active:bg-brand-50 disabled:opacity-40"
                >
                  <Plus size={12} className="text-brand-600" /> {d.brand_name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <button
        onClick={onBrowse}
        className="mt-1 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-bold shadow-sm shadow-brand-600/25 active:bg-brand-700"
      >
        <LayoutGrid size={15} /> Browse saved catalogue
      </button>

      {drugCount > 0 && (
        <button
          onClick={onFocusSearch}
          className="flex items-center gap-1.5 text-xs text-sage-500 font-semibold bg-white ring-1 ring-sage-200 px-3 py-1.5 rounded-full active:bg-sage-50"
        >
          <Package size={11} />
          {drugCount.toLocaleString()} medicine{drugCount === 1 ? '' : 's'} available offline
        </button>
      )}

      {hasReceipt && (
        <button
          onClick={onShare}
          className="mt-1 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white ring-1 ring-sage-200 shadow-sm text-sm font-semibold text-sage-700 active:bg-sage-50"
        >
          <Share2 size={15} />
          Share last receipt
          <span className="text-accent font-bold tabular-nums">{cedi(receipt.payload.total_amount)}</span>
        </button>
      )}

      {onOpenReceipts && (
        <button
          onClick={onOpenReceipts}
          className="flex items-center gap-2 text-[12px] font-semibold text-brand-600 active:text-brand-700"
        >
          <Receipt size={13} /> View past sales <ChevronRight size={13} />
        </button>
      )}
    </div>
  )
}

/** Desktop-only catalogue panel (lg+). Phones browse via BrowseSheet; on a
 *  laptop there's room to keep the catalogue on screen next to the register,
 *  and it filters live as the cashier types in the search box above. The grid
 *  renders at most CATALOGUE_CAP cards so a large catalogue stays smooth. */
const CATALOGUE_CAP = 120

function CataloguePanel({ drugs, query, results, favIds, onAdd, onToggleFav }) {
  const sorted = useMemo(() => [...drugs].sort(byName), [drugs])
  const favs = useMemo(() => sorted.filter(d => favIds.has(d.drug_id)), [sorted, favIds])
  const list = query ? results : sorted
  const shown = list.slice(0, CATALOGUE_CAP)
  const hidden = list.length - shown.length

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="shrink-0 px-4 pt-3.5 pb-2.5 flex items-center justify-between border-b border-sage-200">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-sage-600">
          {query ? `Results for “${query}”` : 'Saved catalogue'}
        </p>
        <p className="text-xs text-sage-500 font-medium tabular-nums">
          {query ? `${results.length} of ${drugs.length}` : `${drugs.length.toLocaleString()} medicines`}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {!query && favs.length > 0 && (
          <>
            <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-amber-700 flex items-center gap-1">
              <Star size={12} className="fill-amber-400 text-amber-400" /> Favourites
            </p>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-2 mb-4">
              {favs.map(d => (
                <DrugCard key={d.drug_id} d={d} fav onAdd={onAdd} onToggleFav={onToggleFav} />
              ))}
            </div>
            <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-sage-600">All medicines</p>
          </>
        )}
        {shown.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-semibold text-sage-800">No medicines match “{query}”.</p>
            <p className="mt-1 text-sm text-sage-600">Check the spelling, or try the generic name.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
            {shown.map(d => (
              <DrugCard key={d.drug_id} d={d} fav={favIds.has(d.drug_id)} onAdd={onAdd} onToggleFav={onToggleFav} />
            ))}
          </div>
        )}
        {hidden > 0 && (
          <p className="text-center text-xs font-medium text-sage-600 pt-4 pb-1">
            Showing {shown.length} of {list.length.toLocaleString()}. Type in the search box to narrow the list.
          </p>
        )}
      </div>
    </div>
  )
}

function DrugCard({ d, fav, onAdd, onToggleFav }) {
  const stock = Number(d.total_stock) || 0
  return (
    <div className={`relative rounded-xl ring-1 ring-sage-200 bg-white ${stock <= 0 ? 'opacity-45' : 'hover:ring-brand-500/40'}`}>
      <button
        onClick={() => onAdd(d)}
        disabled={stock <= 0}
        className="block w-full text-left p-3"
      >
        <p className="text-sm font-bold text-sage-900 truncate pr-7">{d.brand_name}</p>
        <p className="text-xs text-sage-500 truncate">
          {[d.generic_name, [d.dosage_form, d.strength].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
        </p>
        <div className="mt-1.5 flex items-baseline justify-between">
          <span className="text-sm font-black text-accent tabular-nums">{cedi(priceForUnit(d, defaultUnit(d)))}</span>
          <span className={`text-xs font-bold ${stock <= 0 ? 'text-rose-600' : stock < 20 ? 'text-amber-700' : 'text-sage-400'}`}>
            {stock <= 0 ? 'Out of stock' : `${stock} ${d.base_unit || 'Tab'}`}
          </span>
        </div>
      </button>
      {onToggleFav && (
        <button
          onClick={() => onToggleFav(d.drug_id)}
          className="absolute top-1 right-1 h-8 w-8 grid place-items-center rounded-lg text-sage-400 hover:bg-sage-100"
          aria-label={fav ? `Remove ${d.brand_name} from favourites` : `Add ${d.brand_name} to favourites`}
          aria-pressed={fav}
        >
          <Star size={15} className={fav ? 'fill-amber-400 text-amber-400' : ''} />
        </button>
      )}
    </div>
  )
}

function byName(a, b) {
  return String(a.brand_name || '').localeCompare(String(b.brand_name || ''))
}

function timeAgo(ts) {
  const diff = Date.now() - ts
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`
  return `${Math.floor(diff / 86_400_000)} d ago`
}

// Stable per-install device id (so the desktop can attribute sales to a phone).
function getDeviceId() {
  let id = localStorage.getItem('pwa_device_id')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('pwa_device_id', id)
  }
  return id
}
